// src/App.js

import React, { useState, useEffect, useRef } from 'react';
import './index.css';

// --- アニメーション用のカスタムフック ---
// 要素が画面に入ったかどうかを判定する
const useInView = (options) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, options);

    const currentRef = ref.current; // 警告解決策: ref.currentを変数にコピー
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef); // クリーンアップで変数を使用
      }
    };
  }, [ref, options]); // 依存配列は変更なし

  return [ref, isInView];
};

const translations = {
ja: {
    logo: "国際健康協会",
    nav: {
      features: "製品の特長",
      testimonials: "プロモーションビデオ",
      access: "アクセス",
    },
    hero: {
      slides: [
        { title: "宇宙のエネルギーが、すべての生命体を育みます", description: "学生さんの学習集中力アップに / 高齢者疾病全般に / ストレス解消・脳疾患予防と対策に / 物忘れが激しい方に / 痛み全般解消" },
        { title: "簡単装着", description: "手首に装着するだけで、最先端の波動技術を手軽に体験できます。日常生活を送りながら、心身のバランスを整えます。" },
        { title: "各種機器のご案内", description: "お試しは1回60分 5,000円（税込）から。専門のスタッフが丁寧にご案内いたします。お気軽にご相談ください。" }
      ],
      cta: "製品を見る",
    },
    features: {
      title: "製品の特長",
      card1_title: "EMA素粒子 波動発生装置",
      card1_text: "大自然のエネルギーを人工的に発生させ、生命体に必要なエネルギーで欠乏しているエネルギーを自らの吸収力で体内に取り込むことで、健康管理及び疾病対策に利用します。",
      card2_title: "簡単装着",
      card2_text: "手首に装着するだけで、最先端の波動技術を手軽に体験できます。日常生活を送りながら、心身のバランスを整えます。",
      card3_title: "各種機器のご案内",
      card3_text: "お試しは1回60分 5,000円（税込）から。専門のスタッフが丁寧にご案内いたします。お気軽にご相談ください。",
    },
    testimonials: {
      title: "プロモーションビデオ",
      video1_title: "日本語版",
      video1_text: "こちらは日本語版のプロモーションビデオです。",
      video2_title: "中文版",
      video2_text: "こちらは中文版のプロモーションビデオです。海外のお客様からも高い評価をいただいております。",
      video_error: "お使いのブラウザはビデオ再生に対応していません。"
    },
    access: {
      title: "アクセス",
      alt: "アクセスマップ",
      org_name: "国際健康協会",
      traffic_title: "交通案内",
      traffic_text: "東長崎駅 北口2分（JR池袋駅から西武池袋線に乗換してください）",
      reception_title: "受付",
      tel: "電話: 03-6905-8228",
      mobile: "携帯: 080-3271-8681",
    },
    footer: {
      company_name: "国際健康協会",
      tel: "電話番号: 03-6905-8228",
      follow: "Follow Us",
      copyright: "&copy; 2025 国際健康協会. All Rights Reserved.",
    }
  },
zh: {
    logo: "国际健康协会",
    nav: {
      features: "产品特点",
      testimonials: "宣传视频",
      access: "交通指南",
      contact: "联系我们",
    },
    hero: {
      slides: [
        { title: "宇宙能量，孕育生命活力", description: "提升学生学习专注力 / 辅助改善老年人常见健康问题 / 缓解压力，预防脑部疾病 / 改善健忘 / 缓解各类疼痛" },
        { title: "佩戴简单", description: "仅需佩戴于手腕，即可轻松体验尖端波动技术。在日常生活中，轻松调整身心平衡。" },
        { title: "各种设备介绍", description: "体验价为每次60分钟5000日元（含税）。我们的专业人员将为您提供详细的介绍，欢迎随时咨询。" }
      ],
      cta: "查看产品",
    },
    features: {
      title: "产品特点",
      card1_title: "EMA基本粒子 波动发生装置",
      card1_text: "用仪器产生源于大自然的能量，让生命体凭借自身的吸收能力，补充所缺乏的必要能量，从而达到健康管理与疾病预防的效果。",
      card2_title: "佩戴简单",
      card2_text: "仅需佩戴于手腕，即可轻松体验尖端波动技术。在日常生活中，轻松调整身心平衡。",
      card3_title: "各种设备介绍",
      card3_text: "体验价为每次60分钟5000日元（含税）。我们的专业人员将为您提供详细的介绍，欢迎随时咨询。",
    },
    testimonials: {
      title: "宣传视频",
      video1_title: "日文版",
      video1_text: "这是日文版的宣传视频。",
      video2_title: "中文版",
      video2_text: "这是中文版的宣传视频，本产品也受到了海外客户的高度评价。",
      video_error: "您的浏览器不支持视频播放。",
    },
    access: {
      title: "交通指南",
      alt: "访问地图",
      org_name: "国际健康协会",
      traffic_title: "交通路线",
      traffic_text: "JR池袋站换乘西武池袋线, 在东长崎站下车, 北口出站步行2分钟",
      reception_title: "接待",
      tel: "电话: 03-6905-8228",
      mobile: "手机: 080-3271-8681",
    },
    footer: {
      company_name: "国际健康协会",
      address: "东京都新宿区歌舞伎町三宫神社大厦3F",
      tel: "电话号码: 03-6905-8228",
      hours: "营业时间: 11:00〜19:00 (休息日: 星期三)",
      follow: "关注我们",
      copyright: "© 2025 国际健康协会. All Rights Reserved.",
    }
  }
};

// --- 部品（コンポーネント）群 ---

// ヘッダーとナビゲーション
// <li><a href="#contact">{text.nav.contact}</a></li>

function Header({ text, currentLang, onLangChange }) {
  return (
    <header className="lp-header">
      <h1 className="lp-logo">{text.logo}</h1>
      <nav className="lp-nav">
        <ul>
          <li><a href="#features">{text.nav.features}</a></li>
          <li><a href="#testimonials">{text.nav.testimonials}</a></li>
          <li><a href="#access">{text.nav.access}</a></li>
        </ul>
      </nav>
      <div className="language-switcher">
        <button onClick={() => onLangChange('ja')} className={currentLang === 'ja' ? 'active' : ''}>日本語</button>
        <button onClick={() => onLangChange('zh')} className={currentLang === 'zh' ? 'active' : ''}>中文</button>
      </div>
    </header>
  );
}

// ヒーローセクション（メインビジュアル・スライドショー）
function Hero({ text }) {
  const slideImages = ['/images/14.png', '/images/3.jpg', '/images/8.jpg'];
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slideImages.length);
    }, 5000);

    return () => clearInterval(slideInterval);
  }, [slideImages.length]);

  return (
<section className="hero">
      <div className="slideshow-container">
        {slideImages.map((image, index) => (
          <div 
            key={index} 
            className={`slide-item ${index === currentSlide ? 'active' : ''}`}
            style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${image})` }}
          />
        ))}
      </div>
      <div className="hero-content">
        <h2>{text.hero.slides[currentSlide].title}</h2>
        <p>{text.hero.slides[currentSlide].description}</p>
        <a href="#features" className="cta-button">{text.hero.cta}</a>
      </div>
    </section>
  );
}

// 製品の特長セクション
function Features({ text }) {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  return (
    <section id="features" ref={ref} className={`feature-section ${isInView ? 'in-view' : ''}`}>
      <h2 className="section-title">{text.features.title}</h2>
      <div className="feature-grid">
        <div className="feature-card">
          <img src="/images/14.png" alt={text.features.card1_title} />
          <h3>{text.features.card1_title}</h3>
          <p>{text.features.card1_text}</p>
        </div>
        <div className="feature-card">
          <img src="/images/7.jpg" alt={text.features.card2_title} />
          <h3>{text.features.card2_title}</h3>
          <p>{text.features.card2_text}</p>
        </div>
        <div className="feature-card">
          <img src="/images/8.jpg" alt={text.features.card3_title} />
          <h3>{text.features.card3_title}</h3>
          <p>{text.features.card3_text}</p>
        </div>
      </div>
    </section>
  );
}

// プロモーションビデオ（動画）セクション
function Testimonials({ text }) {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  return (
    <section id="testimonials" ref={ref} className={`testimonial-section ${isInView ? 'in-view' : ''}`}>
      <h2 className="section-title">{text.testimonials.title}</h2>
      <div className="video-container">
        <div className="video-item">
          <h3>{text.testimonials.video1_title}</h3>
          <video src="/images/10.mp4" controls poster="/images/13.PNG">
            {text.testimonials.video_error}
          </video>
          <p>{text.testimonials.video1_text}</p>
        </div>
        <div className="video-item">
          <h3>{text.testimonials.video2_title}</h3>
          <video src="/images/9.mp4" controls poster="/images/12.PNG">
            {text.testimonials.video_error}
          </video>
          <p>{text.testimonials.video2_text}</p>
        </div>
      </div>
    </section>
  );
}

// アクセスセクション
function Access({ text }) {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  return (
    <section id="access" ref={ref} className={`access-section ${isInView ? 'in-view' : ''}`}>
      <h2 className="section-title">{text.access.title}</h2>
      <div className="access-content">
        <div className="access-map">
          <img src="/images/5.jpg" alt={text.access.alt} />
        </div>
        <div className="access-info">
          <h3>{text.access.org_name}</h3>
          <p><strong>{text.access.traffic_title}:</strong> {text.access.traffic_text}</p>
          <p><strong>{text.access.reception_title}:</strong></p>
          <p>{text.access.tel}</p>
          <p>{text.access.mobile}</p>
        </div>
      </div>
    </section>
  );
}

// フッター
function Footer({ text }) {
  return (
    <footer className="lp-footer">
      <div className="footer-content">
        <div className="footer-info">
          <h4>{text.footer.company_name}</h4>
          <p>
          {text.footer.tel}</p>
        </div>
        <div className="footer-social">
          <h4>{text.footer.follow}</h4>
          {/* ここに将来的にSNSリンクを追加できます */}
        </div>
      </div>
      <div className="footer-bottom">
        <small dangerouslySetInnerHTML={{ __html: text.footer.copyright }} />      </div>
    </footer>
  );
}

// アプリケーション本体
function App() {
  const [language, setLanguage] = useState('ja');

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
  };

  const currentText = translations[language];

  return (
    <>
      <Header text={currentText} currentLang={language} onLangChange={handleLanguageChange} />
      <main>
        <Hero text={currentText} />
        <Features text={currentText} />
        <Testimonials text={currentText} />
        <Access text={currentText} />
      </main>
      <Footer text={currentText} />
    </>
  );
}

export default App;